import type { ButtonVariant } from '@lightspeed/design-system-react'
import type { Button } from '@lightspeed/unified-components-helios-theme/react'
import { useT } from '@transifex/react'
import type { ComponentProps } from 'react'
import { useCallback, useEffect } from 'react'
import { useSaleFulfilment } from '../../hook/useSaleFulfilment'
import {
  HEAP_DATA_TRACK_ADD_ADDRESS,
  HEAP_DATA_TRACK_EDIT_ADDRESS,
} from '../../../../sale-model.constant'
import { EditAddressButtons } from './EditAddressButton.type'
import type { EditAddressButtonProps } from './EditAddressButton.type'

export type UnifiedButtonAppearance = NonNullable<
  ComponentProps<typeof Button>['appearance']
>

const legacyVariantToUnifiedAppearance: Record<
  ButtonVariant,
  UnifiedButtonAppearance
> = {
  go: 'primary',
  no: 'danger',
  supplementary: 'secondary',
}

export const getUnifiedButtonAppearance = ({
  hasCustomText,
  isAddType,
  variant,
}: {
  hasCustomText: boolean
  isAddType: boolean
  variant?: EditAddressButtonProps['variant']
}): UnifiedButtonAppearance => {
  if (variant) return legacyVariantToUnifiedAppearance[variant]
  if (!hasCustomText) return isAddType ? 'ghost' : 'icon-secondary'
  return 'secondary'
}

export const useEditAddressButtonModel = ({
  type,
  isModalOpeningOnError,
  hasError,
  text,
  variant,
  onClick,
  onOpenModal,
  lineItemLocalId,
}: EditAddressButtonProps) => {
  const t = useT()
  const { isDisabled } = useSaleFulfilment(lineItemLocalId || '')
  const isAddType =
    type === EditAddressButtons.add || type === EditAddressButtons.addShipping
  const openModal = useCallback(onOpenModal, [onOpenModal])

  const handleClick = useCallback(() => {
    onClick?.()
    openModal()
  }, [onClick, openModal])

  useEffect(() => {
    if (hasError && isModalOpeningOnError) {
      openModal()
    }
  }, [hasError, isModalOpeningOnError, openModal])

  return {
    addAddressLabel: t('Add address', { _context: 'Action link' }),
    ariaLabel: isAddType
      ? t('Add address', { _context: 'Action link' })
      : t('Edit address', { _context: 'Action link' }),
    appearance: getUnifiedButtonAppearance({
      hasCustomText: Boolean(text),
      isAddType,
      variant,
    }),
    dataTrack:
      type === EditAddressButtons.edit
        ? HEAP_DATA_TRACK_EDIT_ADDRESS
        : HEAP_DATA_TRACK_ADD_ADDRESS,
    handleClick,
    isAddType,
    isDisabled,
  }
}
